import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle, Circle, Download, ExternalLink, RotateCw, Search, Eye } from "lucide-react";
import { api } from "@/api/client";

// 2026-10-27 owner：「刪除後…點開來預覽 會有一個彈跳視窗 開始逐步抓取對應的外部資料回傳 然後在那一個浮動式窗 我可以透過 HTML 拼湊預覽此頁面」
// Steps: where it is stored -> each NCB chunk (read live on the server) -> join + gunzip in the browser -> SHA-256 vs the
// fingerprint recorded at archive time -> render in a sandboxed iframe (no scripts). Read-only.

export interface ArchiveInfo {
  ncb_instance: string; ncb_rows: string[]; chunk_count: number; html_sha256: string; html_bytes: number; gzip_bytes: number;
  archived_at: string | null; drive_status: string | null; drive_path: string | null;
}
export interface ArchivePage {
  task_id: string; url: string; fetched_at: string; keyword: string | null; location_code: number | null; language_code: string | null;
  project_id: string | null; has_local: boolean; local_bytes: number; archive: ArchiveInfo | null;
}
type StepState = "wait" | "run" | "ok" | "fail";
interface Step { label: string; state: StepState; note?: string }

const fmt = (n: number) => n.toLocaleString("en-US");
const toBytes = (b64: string) => { const s = atob(b64); const u = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i); return u; };
const hex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, "0")).join("");
async function gunzip(data: Uint8Array): Promise<ArrayBuffer> {
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream("gzip"));
  return await new Response(stream).arrayBuffer();
}
// <base> so relative images / CSS resolve against the original site; the iframe sandbox still blocks every script.
const withBase = (html: string, url: string) =>
  /<head[^>]*>/i.test(html) ? html.replace(/<head[^>]*>/i, m => `${m}<base href="${url}">`) : `<base href="${url}">${html}`;

export function HtmlArchivePreview({ page, open, onOpenChange }: { page: ArchivePage | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [steps, setSteps] = useState<Step[]>([]);
  const [html, setHtml] = useState<string | null>(null);
  const [match, setMatch] = useState<boolean | null>(null);
  const [failedAt, setFailedAt] = useState<number | null>(null);
  const bytesRef = useRef<ArrayBuffer | null>(null);
  const runId = useRef(0);

  const start = async (p: ArchivePage) => {
    const id = ++runId.current;
    const a = p.archive;
    setHtml(null); setMatch(null); setFailedAt(null); bytesRef.current = null;
    if (!a) { setSteps([{ label: "查詢存放位置：這頁沒有 NCB 備份紀錄", state: "fail" }]); setFailedAt(0); return; }
    const list: Step[] = [
      { label: `查詢存放位置：NCB ${a.ncb_instance}，共 ${a.chunk_count} 段`, state: "ok" },
      ...a.ncb_rows.map((r, i) => ({ label: `取回第 ${i + 1}／${a.chunk_count} 段（列號 ${r}）`, state: "wait" as StepState })),
      { label: `拼接 ${a.chunk_count} 段 → 解壓縮 → 還原成 HTML（應為 ${fmt(a.html_bytes)} bytes）`, state: "wait" },
      { label: `比對指紋：${a.html_sha256.slice(0, 16)}…`, state: "wait" },
      { label: "在下方畫出頁面", state: "wait" },
    ];
    setSteps(list);
    const set = (i: number, state: StepState, note?: string) => {
      if (runId.current !== id) return;
      list[i] = { ...list[i], state, note }; setSteps([...list]);
    };
    const parts: string[] = [];
    for (let i = 0; i < a.chunk_count; i++) {
      set(i + 1, "run");
      try {
        const r = await api.get<{ payload: string; chars: number; ms: number }>(`/data-browser/html-archive/page/${p.task_id}/chunk/${i}`);
        parts.push(r.data!.payload);
        set(i + 1, "ok", `${fmt(r.data!.chars)} 字元，${r.data!.ms} ms`);
      } catch (e) { set(i + 1, "fail", (e as Error).message); setFailedAt(i + 1); return; }
    }
    const j = a.chunk_count + 1;
    set(j, "run");
    let buf: ArrayBuffer;
    try { buf = await gunzip(toBytes(parts.join(""))); set(j, "ok", `${fmt(buf.byteLength)} bytes`); }
    catch (e) { set(j, "fail", "解壓縮失敗：" + (e as Error).message); setFailedAt(j); return; }
    set(j + 1, "run");
    const sha = hex(await crypto.subtle.digest("SHA-256", buf));
    const ok = sha === a.html_sha256;
    set(j + 1, ok ? "ok" : "fail", ok ? "與備份時的指紋相同" : `不同：${sha.slice(0, 16)}…`);
    if (runId.current !== id) return;
    setMatch(ok); bytesRef.current = buf;
    setHtml(withBase(new TextDecoder("utf-8").decode(buf), p.url));
    set(j + 2, "ok");
  };

  useEffect(() => { if (open && page) start(page); }, [open, page?.task_id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const blobUrl = (b: ArrayBuffer) => URL.createObjectURL(new Blob([b], { type: "text/html;charset=utf-8" }));
  const download = () => {
    if (!bytesRef.current || !page) return;
    const a = document.createElement("a"); a.href = blobUrl(bytesRef.current); a.download = `${page.task_id}.html`; a.click();
  };
  // A blob: URL has the app's origin, so the archived page must never run as-is there (its scripts could read the login token):
  // the new tab is a script-free wrapper holding the page in a sandboxed iframe.
  const openTab = () => {
    if (!html || !page) return;
    const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    const wrapper = `<!doctype html><meta charset="utf-8"><title>${esc(page.url)}</title>`
      + `<style>html,body,iframe{margin:0;width:100%;height:100%;border:0}</style><iframe sandbox="" srcdoc="${esc(html)}"></iframe>`;
    window.open(URL.createObjectURL(new Blob([wrapper], { type: "text/html;charset=utf-8" })), "_blank", "noopener");
  };
  const icon = (s: StepState) => s === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : s === "fail" ? <XCircle className="h-4 w-4 text-destructive" />
    : s === "run" ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Circle className="h-4 w-4 text-muted-foreground/50" />;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-5xl flex-col gap-3 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="truncate pr-6 text-base">預覽外部備份：{page?.url}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            {page?.keyword && <span>字詞 {page.keyword}</span>}
            <span>抓取 {page?.fetched_at?.slice(0, 16).replace("T", " ")}</span>
            <Badge variant="outline">{page?.has_local ? "本機：有" : "本機：已清除"}</Badge>
            <Badge variant="outline">NCB：{page?.archive ? "有" : "無"}</Badge>
            <Badge variant="outline">Drive：{page?.archive?.drive_status === "VERIFIED" ? "有" : "無"}</Badge>
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-1 text-sm">
          {steps.map((s, i) => (
            <li key={i} className="flex items-center gap-2">
              {icon(s.state)}<span className={s.state === "fail" ? "text-destructive" : ""}>{i + 1}. {s.label}</span>
              {s.note && <span className="text-xs text-muted-foreground">｜{s.note}</span>}
            </li>
          ))}
        </ol>
        {html && (
          <iframe title="archived page" sandbox="" srcDoc={html} className="min-h-[50vh] w-full flex-1 rounded border bg-white" />
        )}
        <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-sm">
          {match === true && <span className="font-medium text-emerald-700">指紋一致 ✅ 可回溯</span>}
          {match === false && <span className="font-medium text-destructive">指紋不一致：此備份不可當作原頁</span>}
          {page?.archive?.drive_path && <span className="truncate text-xs text-muted-foreground">Drive：{page.archive.drive_path}</span>}
          <span className="ml-auto flex gap-2">
            {failedAt !== null && page && <Button size="sm" variant="outline" onClick={() => start(page)}><RotateCw className="mr-1 h-4 w-4" />重試</Button>}
            <Button size="sm" variant="outline" disabled={!html} onClick={openTab}><ExternalLink className="mr-1 h-4 w-4" />開新分頁看全頁</Button>
            <Button size="sm" disabled={!html || match !== true} onClick={download}><Download className="mr-1 h-4 w-4" />下載 HTML</Button>
          </span>
        </div>
        <p className="text-xs text-muted-foreground">畫面是存檔的原始 HTML（不執行網頁程式）；對方網站已移除的圖片或樣式可能缺漏，文字與結構完整。</p>
      </DialogContent>
    </Dialog>
  );
}

const REGION: Record<number, string> = { 2158: "TW", 2840: "US", 2392: "JP", 2826: "GB", 2124: "CA", 2036: "AU", 2702: "SG", 2344: "HK" };

// Search a keyword / URL, see where each HTML version is stored, open the step-by-step preview.
export function HtmlArchiveSearchCard() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<ArchivePage[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<ArchivePage | null>(null);
  const search = async () => {
    if (q.trim().length < 2) { setErr("請至少輸入 2 個字"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.get<{ items: ArchivePage[] }>(`/data-browser/html-archive/search?q=${encodeURIComponent(q.trim())}&limit=100`);
      setItems(r.data!.items);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const mark = (ok: boolean, label?: string) => <span className={ok ? "text-emerald-700" : "text-muted-foreground"}>{ok ? "✅" : "—"}{label ? ` ${label}` : ""}</span>;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">查 HTML 存放位置與預覽</CardTitle>
        <CardDescription>輸入字詞或網址片段；每個 HTML 版本顯示本機／NCB／Google Drive 是否都有。按「預覽」會從 NCB 逐段取回、拼回原頁並比對指紋（只讀）。</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form className="flex max-w-xl gap-2" onSubmit={e => { e.preventDefault(); search(); }}>
          <Input placeholder="例：pneumonic plague 或 aol.com" value={q} onChange={e => setQ(e.target.value)} />
          <Button type="submit" disabled={busy}>{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Search className="mr-1 h-4 w-4" />}查詢</Button>
        </form>
        {err && <p className="text-sm text-destructive">{err}</p>}
        {items && (
          <div className="max-h-[28rem] overflow-auto">
            <p className="mb-2 text-xs text-muted-foreground">找到 {items.length} 個 HTML 版本{items.length >= 100 ? "（只顯示前 100 個，請縮小範圍）" : ""}</p>
            <Table>
              <TableHeader><TableRow>
                <TableHead>字詞</TableHead><TableHead>地區</TableHead><TableHead>網址</TableHead><TableHead>抓取</TableHead>
                <TableHead>本機</TableHead><TableHead>NCB</TableHead><TableHead>Drive</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map(p => (
                  <TableRow key={p.task_id}>
                    <TableCell className="max-w-[12rem] truncate">{p.keyword ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">{p.location_code ? (REGION[p.location_code] ?? p.location_code) : "—"}／{p.language_code ?? "—"}</TableCell>
                    <TableCell className="max-w-[22rem] truncate" title={p.url}>{p.url}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{p.fetched_at?.slice(0, 10)}</TableCell>
                    <TableCell className="whitespace-nowrap">{mark(p.has_local, p.has_local ? "" : "已清除")}</TableCell>
                    <TableCell className="whitespace-nowrap">{mark(!!p.archive, p.archive ? `${p.archive.chunk_count} 段` : "")}</TableCell>
                    <TableCell className="whitespace-nowrap">{mark(p.archive?.drive_status === "VERIFIED")}</TableCell>
                    <TableCell><Button size="sm" variant="outline" disabled={!p.archive} onClick={() => setSel(p)}><Eye className="mr-1 h-4 w-4" />預覽</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <HtmlArchivePreview page={sel} open={!!sel} onOpenChange={o => { if (!o) setSel(null); }} />
      </CardContent>
    </Card>
  );
}
